import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { subDays, format, startOfWeek, endOfWeek } from "date-fns";
import { toast } from "sonner";

export interface VitalsData {
  heart_rate: number | null;
  heart_rate_status: string | null;
  heart_rate_trend: string | null;
  systolic_bp: number | null;
  diastolic_bp: number | null;
  bp_status: string | null;
  bp_trend: string | null;
  blood_glucose: number | null;
  glucose_status: string | null;
  glucose_trend: string | null;
  oxygen_saturation: number | null;
  spo2_status: string | null;
  spo2_trend: string | null;
  steps_count: number | null;
  steps_goal: number | null;
  sleep_hours: number | null;
  sleep_quality: string | null;
  recorded_at: string;
}

export interface DeviceData {
  id: string;
  device_type: string;
  device_icon: string;
  is_connected: boolean;
  last_sync_at: string;
  battery_level: number | null;
  signal_strength: string | null;
}

export interface ChartDataPoint {
  date: string;
  heartRate: number | null;
  systolic: number | null;
  diastolic: number | null;
  glucose: number | null;
  oxygen: number | null;
  steps: number | null;
}

export interface WeeklySummary {
  avgHeartRate: number;
  avgSystolic: number;
  avgDiastolic: number;
  avgSteps: number;
  avgSleep: number;
  heartRateStatus: "normal" | "warning" | "critical";
  bpStatus: "normal" | "warning" | "critical";
  stepsStatus: "normal" | "warning" | "critical";
  sleepStatus: "normal" | "warning" | "critical";
}

// Helper to determine status based on value ranges
function getHeartRateStatus(hr: number): "normal" | "warning" | "critical" {
  if (hr >= 60 && hr <= 100) return "normal";
  if (hr >= 50 && hr <= 110) return "warning";
  return "critical";
}

function getBpStatus(systolic: number, diastolic: number): "normal" | "warning" | "critical" {
  if (systolic < 120 && diastolic < 80) return "normal";
  if (systolic < 140 && diastolic < 90) return "warning";
  return "critical";
}

function getStepsStatus(steps: number, goal: number): "normal" | "warning" | "critical" {
  const percentage = (steps / goal) * 100;
  if (percentage >= 100) return "normal";
  if (percentage >= 70) return "warning";
  return "critical";
}

function getSleepStatus(hours: number): "normal" | "warning" | "critical" {
  if (hours >= 7 && hours <= 9) return "normal";
  if (hours >= 6 && hours <= 10) return "warning";
  return "critical";
}

export function useWearableData() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch connected devices
  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ["member-devices", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("patient_devices")
        .select("*")
        .eq("member_id", user.id)
        .order("last_sync_at", { ascending: false });

      if (error) throw error;
      return data as DeviceData[];
    },
    enabled: !!user?.id,
  });

  // Fetch latest vitals
  const { data: latestVitals, isLoading: vitalsLoading } = useQuery({
    queryKey: ["member-vitals-latest", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("patient_vitals")
        .select("*")
        .eq("member_id", user.id)
        .order("recorded_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as VitalsData | null;
    },
    enabled: !!user?.id,
  });

  // Fetch vitals history for charts (last 30 days)
  const { data: vitalsHistory, isLoading: historyLoading } = useQuery({
    queryKey: ["member-vitals-history", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("patient_vitals")
        .select("*")
        .eq("member_id", user.id)
        .gte("recorded_at", subDays(new Date(), 30).toISOString())
        .order("recorded_at", { ascending: true });

      if (error) throw error;
      return data as VitalsData[];
    },
    enabled: !!user?.id,
  });

  // Calculate weekly summary
  const weeklySummary: WeeklySummary | null = vitalsHistory && vitalsHistory.length > 0
    ? (() => {
        const weekStart = startOfWeek(new Date());
        const weekEnd = endOfWeek(new Date());
        const weekData = vitalsHistory.filter(v => {
          const date = new Date(v.recorded_at);
          return date >= weekStart && date <= weekEnd;
        });

        if (weekData.length === 0) return null;

        const avgHeartRate = Math.round(
          weekData.reduce((sum, v) => sum + (v.heart_rate || 0), 0) / weekData.length
        );
        const avgSystolic = Math.round(
          weekData.reduce((sum, v) => sum + (v.systolic_bp || 0), 0) / weekData.length
        );
        const avgDiastolic = Math.round(
          weekData.reduce((sum, v) => sum + (v.diastolic_bp || 0), 0) / weekData.length
        );
        const avgSteps = Math.round(
          weekData.reduce((sum, v) => sum + (v.steps_count || 0), 0) / weekData.length
        );
        const avgSleep = parseFloat(
          (weekData.reduce((sum, v) => sum + (v.sleep_hours || 0), 0) / weekData.length).toFixed(1)
        );

        return {
          avgHeartRate,
          avgSystolic,
          avgDiastolic,
          avgSteps,
          avgSleep,
          heartRateStatus: getHeartRateStatus(avgHeartRate),
          bpStatus: getBpStatus(avgSystolic, avgDiastolic),
          stepsStatus: getStepsStatus(avgSteps, 10000),
          sleepStatus: getSleepStatus(avgSleep),
        };
      })()
    : null;

  // Format chart data
  const chartData: ChartDataPoint[] = vitalsHistory?.map(v => ({
    date: format(new Date(v.recorded_at), "MMM d"),
    heartRate: v.heart_rate,
    systolic: v.systolic_bp,
    diastolic: v.diastolic_bp,
    glucose: v.blood_glucose,
    oxygen: v.oxygen_saturation,
    steps: v.steps_count,
  })) || [];

  // Sync device data - simulates fetching new data from wearable
  const syncDevicesMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");

      // Check if user has devices
      const { data: existingDevices } = await supabase
        .from("patient_devices")
        .select("id")
        .eq("member_id", user.id);

      // If no devices, create a sample device
      if (!existingDevices || existingDevices.length === 0) {
        const { error: deviceError } = await supabase
          .from("patient_devices")
          .insert({
            member_id: user.id,
            device_type: "Apple Watch Series 9",
            device_icon: "⌚",
            is_connected: true,
            last_sync_at: new Date().toISOString(),
            battery_level: 85,
            signal_strength: "strong",
          });
        if (deviceError) throw deviceError;
      } else {
        // Update existing device sync time
        const { error: updateError } = await supabase
          .from("patient_devices")
          .update({ 
            last_sync_at: new Date().toISOString(),
            is_connected: true 
          })
          .eq("member_id", user.id);
        if (updateError) throw updateError;
      }

      // Generate realistic vitals data
      const generateVitals = () => ({
        member_id: user.id,
        heart_rate: Math.floor(Math.random() * 20) + 65, // 65-85
        heart_rate_status: "normal",
        heart_rate_trend: Math.random() > 0.5 ? "+2 bpm" : "-1 bpm",
        systolic_bp: Math.floor(Math.random() * 20) + 110, // 110-130
        diastolic_bp: Math.floor(Math.random() * 15) + 70, // 70-85
        bp_status: "normal",
        bp_trend: "-2 mmHg",
        blood_glucose: Math.floor(Math.random() * 20) + 85, // 85-105
        glucose_status: "normal",
        glucose_trend: "stable",
        oxygen_saturation: Math.floor(Math.random() * 3) + 96, // 96-99
        spo2_status: "normal",
        spo2_trend: "stable",
        steps_count: Math.floor(Math.random() * 5000) + 6000, // 6000-11000
        steps_goal: 10000,
        sleep_hours: parseFloat((Math.random() * 2 + 6).toFixed(1)), // 6-8
        sleep_quality: Math.random() > 0.3 ? "good" : "fair",
        recorded_at: new Date().toISOString(),
      });

      // Insert new vitals reading
      const { error: vitalsError } = await supabase
        .from("patient_vitals")
        .insert(generateVitals());

      if (vitalsError) throw vitalsError;
    },
    onSuccess: () => {
      toast.success("Device synced successfully!");
      queryClient.invalidateQueries({ queryKey: ["member-devices"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-latest"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-history"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to sync device");
    },
  });

  // Seed historical data for charts
  const seedHistoricalDataMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");

      // Generate 30 days of historical data
      const historicalData = [];
      for (let i = 30; i >= 0; i--) {
        const date = subDays(new Date(), i);
        historicalData.push({
          member_id: user.id,
          heart_rate: Math.floor(Math.random() * 20) + 65,
          heart_rate_status: "normal",
          systolic_bp: Math.floor(Math.random() * 20) + 110,
          diastolic_bp: Math.floor(Math.random() * 15) + 70,
          bp_status: "normal",
          blood_glucose: Math.floor(Math.random() * 20) + 85,
          glucose_status: "normal",
          oxygen_saturation: Math.floor(Math.random() * 3) + 96,
          spo2_status: "normal",
          steps_count: Math.floor(Math.random() * 5000) + 6000,
          steps_goal: 10000,
          sleep_hours: parseFloat((Math.random() * 2 + 6).toFixed(1)),
          sleep_quality: Math.random() > 0.3 ? "good" : "fair",
          recorded_at: date.toISOString(),
        });
      }

      // Also ensure a device exists
      const { data: existingDevices } = await supabase
        .from("patient_devices")
        .select("id")
        .eq("member_id", user.id);

      if (!existingDevices || existingDevices.length === 0) {
        await supabase.from("patient_devices").insert({
          member_id: user.id,
          device_type: "Apple Watch Series 9",
          device_icon: "⌚",
          is_connected: true,
          last_sync_at: new Date().toISOString(),
          battery_level: 85,
          signal_strength: "strong",
        });
      }

      const { error } = await supabase
        .from("patient_vitals")
        .insert(historicalData);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Historical data synced!");
      queryClient.invalidateQueries({ queryKey: ["member-devices"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-latest"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-history"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to sync historical data");
    },
  });

  const hasData = !!latestVitals || (vitalsHistory && vitalsHistory.length > 0);
  const hasDevices = devices && devices.length > 0;

  return {
    // Data
    devices,
    latestVitals,
    vitalsHistory,
    chartData,
    weeklySummary,
    // Loading states
    isLoading: devicesLoading || vitalsLoading || historyLoading,
    devicesLoading,
    vitalsLoading,
    historyLoading,
    // Status
    hasData,
    hasDevices,
    // Actions
    syncDevices: syncDevicesMutation.mutate,
    isSyncing: syncDevicesMutation.isPending,
    seedHistoricalData: seedHistoricalDataMutation.mutate,
    isSeeding: seedHistoricalDataMutation.isPending,
  };
}
